import { Truck } from '@phosphor-icons/react';
import React from 'react';

import { getChannelShipmentLines } from '@api/inventory';
import type { ChannelShipmentLeg, StockTransferLine } from '@api/inventory';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import { formatCount } from '@utils/currency';

import TransferLinesModal from './TransferLinesModal';

interface ChannelShipmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: string;
    skuLabel?: string;
    /** The channel's abbreviation key, as the grids group columns (e.g. Amazon_FBA). */
    channelKey: string;
    /** Which cell was clicked: Scheduled or In Transit. */
    leg: ChannelShipmentLeg;
    /** Narrow to shipments into one FC (a warehouse column in the channel pivot / Snapshot). */
    warehouseName?: string;
}

const LEG_LABEL: Record<ChannelShipmentLeg, string> = {
    SCHEDULED: 'scheduled',
    IN_TRANSIT: 'in transit',
};

/** The one sentence under the table: what this leg means on the channel's side. */
const LEG_NOTE: Record<ChannelShipmentLeg, string> = {
    SCHEDULED:
        'Created on the channel and not yet handed over. Counted as inbound to the channel, not in stock or DoH.',
    IN_TRANSIT:
        "Shipped and not yet booked into the channel's stock; a shipment being received shows what is still outstanding. Counted as inbound to the channel, not in stock or DoH.",
};

/** Amazon's SCREAMING_SNAKE status words, as a person would say them. */
const humanizeStatus = (status: string): string =>
    status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');

/** "12 Oct" — the dates here are days, not moments. */
const shortDay = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—';

/**
 * The open shipments behind an In Transit or Scheduled cell of a channel that reports its own
 * inbound — today the Amazon FBA shipment mirror: one row per shipment carrying this SKU into
 * the channel's FCs, with the channel's own status word and the shipped / received split, so
 * the rows add up to the number that was clicked.
 *
 * Shipment ids are shown, not linked: the Stock Transfers page offers Dispatch / Receive on an
 * STN, and a shipment the channel owns must not be moved from here.
 */
const ChannelShipmentModal: React.FC<ChannelShipmentModalProps> = ({
    isOpen,
    onClose,
    productId,
    skuLabel,
    channelKey,
    leg,
    warehouseName,
}) => {
    const [lines, setLines] = React.useState<StockTransferLine[] | null>(null);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getChannelShipmentLines({ productId, channelKey, leg, warehouseName })
            .then((res) => {
                setLines(res.data ?? []);
                setTotal(res.total ?? 0);
            })
            .catch(() => setLines(null))
            .finally(() => setLoading(false));
    }, [isOpen, productId, channelKey, leg, warehouseName]);

    const channelLabel = channelKey.replace(/_/g, ' ');

    const columns: DataSheetColumn<StockTransferLine>[] = [
        {
            key: 'code',
            header: 'Shipment',
            sticky: true,
            width: 170,
            render: (value) => (
                <span className="font-mono text-xs break-all">{value}</span>
            ),
        },
        {
            key: 'toWarehouse',
            header: 'To',
            width: 120,
            render: (value) => value ?? '—',
        },
        {
            key: 'externalStatus',
            header: 'Status',
            width: 130,
            valueAccessor: (row) => row.externalStatus ?? row.status,
            // A partnered-carrier shipment held at Approved until its pickup day is "Pickup booked",
            // not Amazon's IN_TRANSIT — the units are still on the seller's dock (see stnLabels).
            render: (_value, row) =>
                row.status === 'APPROVED' &&
                row.transportMode === 'AMAZON_PARTNERED_CARRIER' &&
                row.externalStatus &&
                !['WORKING', 'READY_TO_SHIP'].includes(row.externalStatus)
                    ? 'Pickup booked'
                    : humanizeStatus(row.externalStatus ?? row.status),
        },
        {
            // The source's own timeline, where the mirror carries it (Amazon: created from the
            // shipment name, declared ship day from the plan, the FC appointment as expected or
            // arrived, first receipt from the ledger). A dash is "the source has not said yet".
            key: 'approvedAt',
            header: 'Created',
            width: 90,
            valueAccessor: (row) => row.approvedAt ?? '',
            render: (_value, row) => shortDay(row.approvedAt),
        },
        {
            key: 'plannedDispatchDate',
            header: 'Ship by',
            width: 90,
            valueAccessor: (row) => row.plannedDispatchDate ?? '',
            render: (_value, row) => shortDay(row.plannedDispatchDate),
        },
        {
            // Amazon's own term: the dock slot the transporter books at the FC. Booked until the
            // first receipt lands, arrived after. Only a slot Amazon reported is shown — a lane
            // estimate is not a fact about this shipment — so "not booked" is an honest blank.
            key: 'expectedDate',
            header: 'Delivery appointment',
            width: 150,
            valueAccessor: (row) => (row.expectedIsBooked ? row.expectedDate ?? '' : ''),
            render: (_value, row) =>
                row.expectedIsBooked ? (
                    <span>
                        {shortDay(row.expectedDate)}
                        <span className="ml-1 text-xs text-slate-500">· {row.receivedAt ? 'arrived' : 'booked'}</span>
                    </span>
                ) : (
                    <span className="text-xs text-slate-400">not booked</span>
                ),
        },
        {
            key: 'receivedAt',
            header: 'Received from',
            width: 110,
            valueAccessor: (row) => row.receivedAt ?? '',
            render: (_value, row) => shortDay(row.receivedAt),
        },
        {
            key: 'dispatchedQty',
            header: 'Shipped',
            align: 'right',
            width: 90,
            render: (value) => formatCount(value ?? 0),
        },
        {
            key: 'receivedQty',
            header: 'Received',
            align: 'right',
            width: 100,
            render: (value) => formatCount(value ?? 0),
        },
        {
            key: 'openQty',
            header: 'Outstanding',
            align: 'right',
            width: 110,
            render: (value) => (
                <span className="font-semibold">{formatCount(value ?? 0)}</span>
            ),
        },
    ];

    return (
        <TransferLinesModal
            isOpen={isOpen}
            onClose={onClose}
            wide
            icon={<Truck size={18} weight="bold" aria-hidden />}
            title={`${channelLabel} shipments ${LEG_LABEL[leg]}`}
            subtitle={
                <>
                    {skuLabel ?? productId}
                    {warehouseName ? ` · into ${warehouseName}` : ''}
                    {lines
                        ? ` · ${formatCount(total)} units on ${lines.length} shipment${lines.length === 1 ? '' : 's'}`
                        : ''}
                </>
            }
            loading={loading}
            lines={lines}
            columns={columns}
            getRowKey={(line) => `${line.stnId}`}
            emptyText={`No ${channelLabel} shipment is ${LEG_LABEL[leg]} for this SKU right now`}
            footerNote={LEG_NOTE[leg]}
            footerLink={{
                to: '/stock-transfers',
                label: 'Open Stock Transfers →',
            }}
        />
    );
};

export default ChannelShipmentModal;
